import React, { useEffect, useState, useMemo } from "react";
import {
  TextField,
  Button,
  Grid,
  Box,
  Switch,
  FormControlLabel,
  FormControl,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Alert,
  Chip,
} from "@mui/material";
import { useAuth, useSnackbar, useLoading, useNavigation } from "../../../Context/Contexts";
import CenteredCard from "../../../components/Card/CenteredCardLayout";
import { ShippingServiceJsonViewModel } from "../../../Types/ExportTypes";
import { CountryAutoComplete } from "../../../components/AutoComplete/AutoCompletes";
import DefaultActions from "../../../components/Card/DefaultActions";
import ReactJson from "react-json-view";
import { useTranslation } from "react-i18next";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
const ShippingServiceJsonEditPage: React.FC<{ params?: { [key: string]: any } }> = ({ params }) => {
  const { serviceId = 0, id = 0 } = params || {};
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const { showLoading, hideLoading, showThinking, hideThinking } = useLoading();
  const { authService } = useAuth();
  const { showSnackbar } = useSnackbar();
  const { goBack, navigate } = useNavigation();
  const [viewModel, setViewModel] = useState<ShippingServiceJsonViewModel | null>(null);
  const [showVariables, setShowVariables] = useState(false);
  const [hasJsonError, setHasJsonError] = useState(false);
  const [suggestIsOpen, setSuggestIsOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [variablesOpen, setVariablesOpen] = useState(false);

  const variableGroups = useMemo(() => ({
    general: [
      { placeholder: "{SenderId}", label: "Unifaun sender ID" },
      { placeholder: "{ServiceId}", label: "Unifaun service ID" },
      { placeholder: "{ExternalValue1}", label: t("common.externalValue1", { defaultValue: "Extra external value 1" }) },
      { placeholder: "{Partition}", label: t("common.partition", { defaultValue: "Unifaun partition name" }) },
      { placeholder: "{Favorite}", label: t("common.favorite", { defaultValue: "Unifaun print favourite name" }) },
      { placeholder: "{SenderEmail}", label: t("common.senderEmail", { defaultValue: "Company sender e-mail" }) },
      { placeholder: "{DateTime}", label: t("common.dateTime", { defaultValue: "Date and time of label creation" }) }
    ],
    order: [
      { placeholder: "{OrderNo}", label: t("common.orderNo", { defaultValue: "Source document order no." }) },
      { placeholder: "{OrderBatchNo}", label: t("common.orderBatchNo", { defaultValue: "Source document order batch ID" }) },
      { placeholder: "{OrderAmount}", label: t("common.orderAmount", { defaultValue: "CoD amount" }) },
      { placeholder: "{CustomerNo}", label: t("common.customerNo", { defaultValue: "Source document sell to customer no." }) }
    ],
    customer: [
      { placeholder: "{ShipToCustomerName}", label: t("common.shipToCustomerName", { defaultValue: "Ship-to name" }) },
      { placeholder: "{ShipToCustomerName2}", label: t("common.shipToCustomerName2", { defaultValue: "Ship-to name 2" }) },
      { placeholder: "{ShipToCustomerAddress}", label: t("common.shipToCustomerAddress", { defaultValue: "Ship-to address" }) },
      { placeholder: "{ShipToCustomerAddress2}", label: t("common.shipToCustomerAddress2", { defaultValue: "Ship-to address 2" }) },
      { placeholder: "{ShipToCustomerPostCode}", label: t("common.shipToCustomerPostCode", { defaultValue: "Ship-to postcode" }) },
      { placeholder: "{ShipToCustomerCity}", label: t("common.shipToCustomerCity", { defaultValue: "Ship-to city" }) },
      { placeholder: "{ShipToCustomerCounty}", label: t("common.shipToCustomerCounty", { defaultValue: "Ship-to county" }) },
      { placeholder: "{ShipToCustomerCountryCode}", label: t("common.shipToCustomerCountryCode", { defaultValue: "Ship-to country code" }) },
      { placeholder: "{SellToCustomerName}", label: t("common.sellToCustomerName", { defaultValue: "Sell-to name" }) },
      { placeholder: "{SellToCustomerName2}", label: t("common.sellToCustomerName2", { defaultValue: "Sell-to name 2" }) },
      { placeholder: "{SellToCustomerAddress}", label: t("common.sellToCustomerAddress", { defaultValue: "Sell-to address" }) },
      { placeholder: "{SellToCustomerAddress2}", label: t("common.sellToCustomerAddress2", { defaultValue: "Sell-to address 2" }) },
      { placeholder: "{SellToCustomerPostCode}", label: t("common.sellToCustomerPostCode", { defaultValue: "Sell-to postcode" }) },
      { placeholder: "{SellToCustomerCity}", label: t("common.sellToCustomerCity", { defaultValue: "Sell-to city" }) },
      { placeholder: "{SellToCustomerCounty}", label: t("common.sellToCustomerCounty", { defaultValue: "Sell-to county" }) },
      { placeholder: "{SellToCustomerCountryCode}", label: t("common.sellToCustomerCountryCode", { defaultValue: "Sell-to country code" }) },
      { placeholder: "{CustomerContact}", label: t("common.customerContact", { defaultValue: "Ship-to contact name" }) },
      { placeholder: "{CustomerPhone}", label: t("common.customerPhone", { defaultValue: "Customer phone no." }) },
      { placeholder: "{CustomerFax}", label: t("common.customerFax", { defaultValue: "Customer fax no." }) },
      { placeholder: "{CustomerOrgNo}", label: t("common.customerOrgNo", { defaultValue: "Customer organization no." }) },
      { placeholder: "{CustomerDoorcode}", label: t("common.customerDoorcode", { defaultValue: "Ship-to door code" }) },
      { placeholder: "{CustomerEmail}", label: t("common.customerEmail", { defaultValue: "Customer e-mail" }) },
      { placeholder: "{CustomerSMS}", label: t("common.customerSms", { defaultValue: "Ship-to mobile phone no." }) },
      { placeholder: "{CustomerVatNo}", label: t("common.customerVatNo", { defaultValue: "Customer VAT no." }) },
      { placeholder: "{CustomsCurrencyUnit}", label: t("common.customsCurrencyUnit", { defaultValue: "Currency code" }) }
    ],
    parcel: [
      { placeholder: "{ParcelGrossWeight}", label: t("common.parcelGrossWeight", { defaultValue: "Parcel gross weight" }) },
      { placeholder: "{ParcelVolume}", label: t("common.parcelVolume", { defaultValue: "Parcel volume" }) },
      { placeholder: "{ParcelLength}", label: t("common.parcelLength", { defaultValue: "Parcel length" }) },
      { placeholder: "{ParcelWidth}", label: t("common.parcelWidth", { defaultValue: "Parcel width" }) },
      { placeholder: "{ParcelHeight}", label: t("common.parcelHeight", { defaultValue: "Parcel height" }) },
      { placeholder: "{ParcelContents}", label: t("common.parcelContents", { defaultValue: "Parcel contents" }) },
      { placeholder: "{ParcelPackageCode}", label: t("common.parcelPackageCode", { defaultValue: "Parcel package code" }) }
    ]
  }), [t]);


  useEffect(() => {
    if (isLoading) showLoading();
    else hideLoading();
  }, [isLoading, showLoading, hideLoading]);

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      authService
        .post<ShippingServiceJsonViewModel>(`/settingsApi/GetShippingServiceJson?serviceId=${serviceId}&id=${id}`, {})
        .then((data) => {
          let parsedJson = {};
          let error = false;

          try {
            parsedJson = data.json ? JSON.parse(data.json) : {};
          } catch (error: any) {
            console.error("Invalid JSON from API:", error);
            error = true;
            showSnackbar(t("dialog.invalidJsonReset"), "error");
          }

          setViewModel({
            ...data,
            parsedJson, // Set parsed JSON or empty object
          });
          setHasJsonError(error);
        })
        .catch((error: any) => {
          showSnackbar(`${t("common.failedToFetchEntries")}: ${error.response?.data ?? error.message}`, "error");
        })
        .finally(() => setIsLoading(false));
    } else {
      // Default empty location object for creating new entries
      setViewModel({
        createNew: true,
        serviceId: serviceId,
        json: "{}",
        parsedJson: {},
      });
      setHasJsonError(false);
    }
  }, [id, authService]);



  const handleSave = async () => {
    if (!viewModel) return;
    var newItem = viewModel.createNew ?? false;
    if (hasJsonError) {
      showSnackbar(t("dialog.fixJsonBeforeSave"), "warning");
      return;
    }
    const updatedItem = { ...viewModel, json: JSON.stringify(viewModel.parsedJson) };

    setIsLoading(true);
    try {
      const response = await authService.post<ShippingServiceJsonViewModel>("/settingsApi/CreateOrUpdateShippingServiceJson", updatedItem);
      let parsedJson = {};
      let error = false;

      try {
        parsedJson = response.json ? JSON.parse(response.json) : {};
      } catch (err) {
        console.error("Invalid JSON from API:", err);
        error = true;
        showSnackbar(t("dialog.invalidJsonReset"), "error");
      }

      setViewModel({
        ...response,
        parsedJson, // Set parsed JSON or empty object
      });
      setHasJsonError(error);
      showSnackbar(t("dialog.savedSuccessfully"), "success");

      if (newItem) {
        navigate({ path: "/settings/shippingservice/editjson", params: { serviceId: response.serviceId, id: response.id } });
      }
    } catch {
      showSnackbar(t("dialog.saveErrorGeneric"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field: keyof ShippingServiceJsonViewModel, value: any) => {
    setViewModel((prev) => (prev ? { ...prev, [field]: value, hasChanges: true } : null));
  };


  const handleDelete = async () => {

    const isConfirmed = window.confirm(t("dialog.deleteEntityConfirm"));
    if (!isConfirmed) return;
    var newItem = viewModel?.createNew ?? false;
    if (newItem) {
      navigate({ path: "/settings/shippingservice/edit", params: { id: serviceId } });
      return;
    }

    setIsLoading(true);
    try {
      await authService.post(`/settingsApi/DeleteShippingServiceJson`, viewModel);
      showSnackbar(t("dialog.deletedSuccessfully"), "success");

      navigate({ path: "/settings/shippingservice/edit", params: { id: serviceId } });
    } catch (error: any) {
      showSnackbar(`${t("dialog.deleteFailed")} : ${error.response?.data ?? error.message}`, "error");

    } finally {
      setIsLoading(false);
    }
  };

  const handleJsonChange = (edit: { updated_src?: Record<string, any> }) => {
    if (!viewModel) return;

    if (edit.updated_src) {
      setViewModel({ ...viewModel, parsedJson: edit.updated_src });
      setHasJsonError(false);
    }
  };

  const openSuggestDialog = () => {
    setSuggestIsOpen(true);
  };

  const closeSuggestDialog = () => {
    setSuggestIsOpen(false);
    setPrompt("");
  };

  const handleSuggest = async () => {
    if (!viewModel) return;

    if (!prompt?.trim()) {
      showSnackbar(t("dialog.missingPrompt"), "warning");
      return;
    }

    const { parsedJson, ...restViewModel } = viewModel;
    const jsonString = !hasJsonError
      ? JSON.stringify(parsedJson ?? {})
      : viewModel.json ?? "{}";

    try {
      showThinking();
      const response = await authService.post<ShippingServiceJsonViewModel>(
        "/settingsApi/SuggestChangeForJson",
        {
          prompt,
          viewModel: {
            ...restViewModel,
            json: jsonString
          }
        }
      );

      closeSuggestDialog();

      let parsedResponse: Record<string, any> = {};
      let error = false;

      try {
        parsedResponse = response.json ? JSON.parse(response.json) : {};
      } catch (err) {
        error = true;
        showSnackbar(t("dialog.invalidJsonReset"), "error");
      }

      setViewModel({
        ...viewModel,
        ...response,
        parsedJson: parsedResponse,
        hasChanges: true
      });
      setHasJsonError(error);
      showSnackbar(t("common.success"), "success");
    } catch (error: any) {
      showSnackbar(`${t("common.saveError")} : ${error.response?.data ?? error.message}`, "error");
    } finally {
      hideThinking();
    }
  };


  // add section buttons
  const handleAddPdfConfig = () => {
    if (!viewModel?.parsedJson) return;

    const updatedJson = { ...viewModel.parsedJson };

    // Ensure pdfConfig exists
    if (!updatedJson.pdfConfig) {
      updatedJson.pdfConfig = {
        target1Media: "",
        target1XOffset: 0,
        target1YOffset: 0,
        target1LayoutFilter: [],
        target2Media: "",
        target2XOffset: 0,
        target2YOffset: 0,
        target3Media: "",
        target3XOffset: 0,
        target3YOffset: 0,
        target4Media: "",
        target4XOffset: 0,
        target4YOffset: 0,
      };
      setViewModel({ ...viewModel, parsedJson: updatedJson, hasChanges: true });
      showSnackbar(t("dialog.sectionAdded"), "success");
    } else {
      showSnackbar(t("dialog.sectionAlreadyExists"), "info");
    }
  };

  // Function to add a default Shipment if missing
  const handleAddShipment = () => {
    if (!viewModel?.parsedJson) return;

    const updatedJson = { ...viewModel.parsedJson };

    if (!updatedJson.shipment) {
      updatedJson.shipment = {
        sender: {
          quickId: "{SenderId}"
        },
        receiver: {
          quickId: "{CustomerNo}",
          name: "{ShipToCustomerName}",
          address1: "{ShipToCustomerAddress}",
          zipcode: "{ShipToCustomerPostCode}",
          city: "{ShipToCustomerCity}",
          country: "{ShipToCustomerCountryCode}",
          contact: "{CustomerContact}",
          phone: "{CustomerPhone}",
          email: "{CustomerEmail}",
          mobile: "{CustomerSMS}"
        },
        service: {
          id: "P17",
          addons: [
            {
              id: "NOTSMS",
              mobile: "{CustomerSMS}"
            }
          ]
        },
        parcels: [],
        orderNo: "{OrderNo}",
        senderReference: "{OrderBatchNo}"
      };
      setViewModel({ ...viewModel, parsedJson: updatedJson, hasChanges: true });
      showSnackbar(t("dialog.sectionAdded"), "success");
    } else {
      showSnackbar(t("dialog.sectionAlreadyExists"), "info");
    }
  };


  const handleAddAgent = () => {
    if (!viewModel?.parsedJson) return;

    const updatedJson = { ...viewModel.parsedJson };

    // Ensure shipment exists
    if (!updatedJson.shipment) {
      updatedJson.shipment = {};
    }

    // Add agent if missing
    if (!updatedJson.shipment.agent) {
      updatedJson.shipment.agent = {
        quickId: "{SenderId}",
        name: "",
        address1: "",
        zipcode: "",
        city: "",
        country: "",
        contact: "",
        phone: "",
        email: ""
      };
      setViewModel({ ...viewModel, parsedJson: updatedJson, hasChanges: true });
      showSnackbar(t("dialog.sectionAdded"), "success");
    } else {
      showSnackbar(t("dialog.sectionAlreadyExists"), "info");
    }
  };

  const handleAddCustomsDeclaration = () => {
    if (!viewModel?.parsedJson) return;

    const updatedJson = { ...viewModel.parsedJson };

    // Ensure shipment exists before adding customsDeclaration
    if (!updatedJson.shipment) {
      updatedJson.shipment = {};
    }

    if (!updatedJson.shipment.customsDeclaration) {
      updatedJson.shipment.customsDeclaration = {
        exportDeclaration1: "I hereby certify that the information on this invoice is true and correct ",
        exportDeclaration2: "and the contents and value of this shipment is as stated above.",
        invoiceDeclaration1: "I hereby certify that the information on this invoice is true and correct ",
        invoiceDeclaration2: "and the contents and value of this shipment is as stated above",
        importExportType: "Permanent",
        receiver: {
          quickId: "{CustomerNo}",
          name: "{ShipToCustomerName}",
          address1: "{ShipToCustomerAddress}",

          zipcode: "{ShipToCustomerPostCode}",
          city: "{ShipToCustomerCity}",
          country: "{ShipToCustomerCountryCode}",
          contact: "{CustomerContact}",
          phone: "{CustomerPhone}",
          email: "{CustomerEmail}"
        },
        sender: {
          quickId: "{SenderId}"
        },
        invoiceNo: "{OrderNo}",
        declarantDate: "{DateTime2}",
        printSet: ["PROFORMA"],
        termCode: "DAP",
        orderNo: "{OrderNo}",
        senderReference: "{OrderNo}",
        packageCode: "PAK",
        termsCode: "SRR"
      };
      setViewModel({ ...viewModel, parsedJson: updatedJson, hasChanges: true });
      showSnackbar(t("dialog.sectionAdded"), "success");
    } else {
      showSnackbar(t("dialog.sectionAlreadyExists"), "info");
    }
  };


  const openVariablesDialog = () => setVariablesOpen(true);

  const closeVariablesDialog = () => setVariablesOpen(false);


  return (
    <>
      <CenteredCard title={t("common.edit") + " JSON"} isLoading={isLoading} handleSave={handleSave}>
        <Box sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 2
        }}>
          <DefaultActions
            onSave={handleSave}

            onDelete={handleDelete}
            customActions={[
              {
                label: t("common.variables"),
                onClick: openVariablesDialog
              }
            ]}
          />
          <Button variant="contained" onClick={openSuggestDialog}>
            {viewModel?.createNew ? t("common.jsonSuggestAi") : t("common.jsonChangeAi")}
          </Button>
        </Box>

        <Box sx={{ position: "absolute", top: 8, right: 12, display: "flex", gap: 1, alignItems: "center" }}>
          {!!viewModel?.hasChanges && (
            <Chip
              size="small"
              color="warning"
              icon={<WarningAmberOutlinedIcon sx={{ fontSize: 16 }} />}
              label={t("common.unsavedChanges") ?? "Unsaved changes"}
              sx={{
                "@keyframes breathe": {
                  "0%": { opacity: 1 },
                  "50%": { opacity: 0.75 },
                  "100%": { opacity: 1 },
                },
                animation: "breathe 2.5s ease-in-out infinite",
              }}
            />
          )}


        </Box>
        {viewModel && (
          <>
            <Grid container spacing={2}>


              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <CountryAutoComplete
                    key={viewModel.countryCode || null}
                    valueId={viewModel.countryCode || null}
                    onChange={(selected) => handleChange("countryCode", selected?.value || "")}
                    disabled={viewModel.createNew != true}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6">JSON {t("common.editor")}</Typography>
                <ReactJson
                  src={viewModel?.parsedJson ?? {}}
                  onEdit={handleJsonChange}
                  onAdd={handleJsonChange}
                  onDelete={handleJsonChange}
                  theme="monokai"
                  displayDataTypes={false}
                  enableClipboard={true}
                  displayObjectSize={true}
                  indentWidth={4}
                  collapseStringsAfterLength={40}


                />
                {hasJsonError && <Typography color="error">⚠ {t("dialog.invalidJsonBeforeSave")}</Typography>}
              </Grid>



            </Grid>



            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={showVariables} onChange={(e) => setShowVariables(e.target.checked)} />}
                label={t("common.addSections")}
              />
            </Grid>

            {/* Settings Section */}
            {showVariables && (
              <>
                <Box
                  sx={{
                    backgroundColor: "secondary.main",
                    padding: 2,
                    borderRadius: 2,
                    width: "100%"
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Button onClick={handleAddPdfConfig} variant="contained" color="primary">
                        {t("common.addPdfConfig")}
                      </Button>
                    </Grid>
                    <Grid item xs={12}>
                      <Button onClick={handleAddShipment} variant="contained" color="primary">
                        {t("common.addShipment")}
                      </Button>
                    </Grid>
                    <Grid item xs={12}>
                      <Button onClick={handleAddAgent} variant="contained" color="primary">
                        {t("common.addAgent")}
                      </Button>
                    </Grid>

                    <Grid item xs={12}>
                      <Button onClick={handleAddCustomsDeclaration} variant="contained" color="primary">
                        {t("common.addCustomsDeclaration")}
                      </Button>
                    </Grid>



                  </Grid>



                </Box>
              </>
            )}
          </>
        )}

        <Box sx={{ marginTop: 2, textAlign: "center" }}>
          <Button variant="contained" onClick={handleSave} disabled={isLoading} sx={{ marginRight: 2 }}>
            {t("common.save")}
          </Button>
          <Button variant="outlined" onClick={goBack}>
            {t("common.back")}
          </Button>
        </Box>

      </CenteredCard>

      <Dialog open={suggestIsOpen} onClose={closeSuggestDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {viewModel?.createNew ? t("common.jsonSuggestAi") : t("common.jsonChangeAi")}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info" sx={{ mb: 1 }}>
              {t("help.suggestAiJsonHelp")}
            </Alert>
            <TextField
              label={t("common.request")}
              value={prompt}
              InputLabelProps={{ shrink: true }}
              onChange={(e) => setPrompt(e.target.value)}
              fullWidth
              multiline
              minRows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSuggestDialog}>{t("common.cancel")}</Button>
          <Button onClick={handleSuggest} color="success" variant="contained">
            {t("common.run")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={variablesOpen} onClose={closeVariablesDialog} maxWidth="md" fullWidth>
        <DialogTitle>{t("common.variables")}</DialogTitle>
        <DialogContent>
          <Box component="div" sx={{ mt: 1 }}>
            {/* General */}
            <Box component="div" sx={{ mt: 2 }}>
              {variableGroups.general.map(({ placeholder, label }) => (
                <Typography key={placeholder} variant="body2">
                  <strong>{placeholder}</strong> = {label}
                </Typography>
              ))}
            </Box>

            {/* Order */}
            <Box component="div" sx={{ mt: 2 }}>
              {variableGroups.order.map(({ placeholder, label }) => (
                <Typography key={placeholder} variant="body2">
                  <strong>{placeholder}</strong> = {label}
                </Typography>
              ))}
            </Box>

            {/* Customer */}
            <Box component="div" sx={{ mt: 2 }}>
              {variableGroups.customer.map(({ placeholder, label }) => (
                <Typography key={placeholder} variant="body2">
                  <strong>{placeholder}</strong> = {label}
                </Typography>
              ))}
            </Box>

            {/* Parcel */}
            <Box component="div" sx={{ mt: 2 }}>
              {variableGroups.parcel.map(({ placeholder, label }) => (
                <Typography key={placeholder} variant="body2">
                  <strong>{placeholder}</strong> = {label}
                </Typography>
              ))}
            </Box>
          </Box>

        </DialogContent>
        <DialogActions>
          <Button onClick={closeVariablesDialog}>{t("common.close")}</Button>
        </DialogActions>
      </Dialog>

    </>
  );
};

export default ShippingServiceJsonEditPage;
